// P0 Health Operations client library.
//
// Keeps sensitive medical-profile details behind dedicated RPCs and tables.
// Teacher-facing safety cards deliberately expose only action-time information;
// physician, insurance, exemption basis, and private medical conditions remain
// parent/admin-only.

import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const EXEMPTION_BUCKET = "child-health-exemptions";
const SIGNED_URL_TTL = 60 * 60;

const PROFILE_SELECT = `
  child_id, organization_id, family_id,
  medical_conditions, emergency_plan,
  physician_name, physician_phone,
  insurance_provider, insurance_member_id, insurance_group_number,
  updated_by, created_at, updated_at
`;

const EXEMPTION_SELECT = `
  id, organization_id, child_id, family_id, record_type_id,
  basis, notes, expires_on, document_path, status,
  submitted_by, submitted_at, reviewed_by, reviewed_at, review_note,
  revoked_by, revoked_at, revoke_reason, created_at, updated_at,
  record_type:child_health_record_types(id, code, label)
`;

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

export async function listMedicalProfilesForChildren(childIds) {
  if (!isSupabaseConfigured || !childIds?.length) return [];
  const { data, error } = await supabase
    .from("child_medical_profiles")
    .select(PROFILE_SELECT)
    .in("child_id", childIds);
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function upsertMedicalProfile({
  childId,
  medicalConditions,
  emergencyPlan,
  physicianName,
  physicianPhone,
  insuranceProvider,
  insuranceMemberId,
  insuranceGroupNumber,
}) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_upsert_child_medical_profile", {
    p_child_id: childId,
    p_medical_conditions: medicalConditions || null,
    p_emergency_plan: emergencyPlan || null,
    p_physician_name: physicianName || null,
    p_physician_phone: physicianPhone || null,
    p_insurance_provider: insuranceProvider || null,
    p_insurance_member_id: insuranceMemberId || null,
    p_insurance_group_number: insuranceGroupNumber || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

export async function listHealthExemptionsForChildren(childIds) {
  if (!isSupabaseConfigured || !childIds?.length) return [];
  const { data, error } = await supabase
    .from("child_health_exemptions")
    .select(EXEMPTION_SELECT)
    .in("child_id", childIds)
    .order("submitted_at", { ascending: false });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function submitHealthExemption({ childId, recordTypeId, basis, expiresOn, notes }) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_submit_child_health_exemption", {
    p_child_id: childId,
    p_record_type_id: recordTypeId,
    p_basis: basis,
    p_expires_on: expiresOn || null,
    p_notes: notes || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

export async function setHealthExemptionDocument({ exemptionId, path }) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_set_child_health_exemption_document", {
    p_exemption_id: exemptionId,
    p_document_path: path,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

export async function uploadHealthExemptionDocument({ exemptionId, file }) {
  ensureLive();
  if (!file) throw new Error("Pick a file");
  const ext = (file.name.split(".").pop() || "bin").toLowerCase().slice(0, 10);
  const path = `${exemptionId}/${crypto.randomUUID()}.${ext}`;
  const { error: uploadError } = await supabase.storage.from(EXEMPTION_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (uploadError) throw uploadError;
  try {
    await setHealthExemptionDocument({ exemptionId, path });
  } catch (error) {
    await supabase.storage.from(EXEMPTION_BUCKET).remove([path]).catch(() => {});
    throw error;
  }
  return path;
}

export async function getHealthExemptionDocumentUrl(path) {
  if (!isSupabaseConfigured || !path) return null;
  const { data, error } = await supabase.storage
    .from(EXEMPTION_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL);
  if (error) return null;
  return data?.signedUrl || null;
}

export async function reviewHealthExemption({ exemptionId, status, note }) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_review_child_health_exemption", {
    p_exemption_id: exemptionId,
    p_status: status,
    p_note: note || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

export async function getTeacherSafetyCard(childId) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_teacher_child_safety_card", {
    p_child_id: childId,
  });
  if (error) throw error;
  const rows = Array.isArray(data) ? data : [data].filter(Boolean);
  const row = rows[0] || null;
  if (!row) return null;
  return {
    childId: row.out_child_id ?? row.child_id,
    childName: row.out_child_name ?? row.child_name,
    allergies: row.out_allergies ?? row.allergies,
    medicalNotes: row.out_medical_notes ?? row.medical_notes,
    emergencyPlan: row.out_emergency_plan ?? row.emergency_plan,
  };
}

export async function listHealthConfigurationGaps() {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc("rpc_health_configuration_gaps_today");
  if (error) {
    if (["42P01", "42883", "PGRST202"].includes(error.code)) return [];
    throw error;
  }
  return (data || []).map((row) => ({
    childId: row.out_child_id ?? row.child_id,
    childName: row.out_child_name ?? row.child_name,
    classroomId: row.out_classroom_id ?? row.classroom_id,
    classroomName: row.out_classroom_name ?? row.classroom_name,
    recordTypeId: row.out_record_type_id ?? row.record_type_id,
    recordTypeLabel: row.out_record_type_label ?? row.record_type_label,
    reason: row.out_reason ?? row.reason,
  }));
}

export function deriveExemptionStatus(exemption, now = new Date()) {
  if (!exemption) return "none";
  if (exemption.status === "revoked") return "revoked";
  if (exemption.status === "rejected") return "rejected";
  if (exemption.status === "submitted") return "submitted";
  if (exemption.status === "approved") {
    if (exemption.expires_on) {
      const expires = new Date(`${exemption.expires_on}T00:00:00`);
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      if (!Number.isNaN(expires.getTime()) && expires <= today) return "expired";
      const warning = new Date(today);
      warning.setDate(warning.getDate() + 30);
      if (!Number.isNaN(expires.getTime()) && expires <= warning) return "expiring_soon";
    }
    return "approved";
  }
  return exemption.status || "none";
}

export const EXEMPTION_STATUS_META = {
  submitted: { label: "In review", tone: "border-amber-300 bg-amber-50 text-amber-900" },
  approved: { label: "Approved exemption", tone: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  expiring_soon: { label: "Exemption expiring", tone: "border-amber-300 bg-amber-50 text-amber-900" },
  expired: { label: "Exemption expired", tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]" },
  rejected: { label: "Exemption not approved", tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]" },
  revoked: { label: "Exemption revoked", tone: "border-neutral-300 bg-neutral-100 text-neutral-700" },
};

export function activeApprovedExemption(exemptions, recordTypeId) {
  return (exemptions || []).find(
    (row) => row.record_type_id === recordTypeId && deriveExemptionStatus(row) === "approved",
  ) || null;
}
